<script>
import { activeBrand } from '@/themes/primevue/brandState.js';
import { Link } from '@inertiajs/vue3';
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';
import SidebarAside from 'primevue/sidebaraside';
import SidebarContent from 'primevue/sidebarcontent';
import SidebarFooter from 'primevue/sidebarfooter';
import SidebarGroup from 'primevue/sidebargroup';
import SidebarGroupContent from 'primevue/sidebargroupcontent';
import SidebarGroupLabel from 'primevue/sidebargrouplabel';
import SidebarHeader from 'primevue/sidebarheader';
import SidebarMenu from 'primevue/sidebarmenu';
import SidebarMenuButton from 'primevue/sidebarmenubutton';
import SidebarMenuItem from 'primevue/sidebarmenuitem';
import SidebarPanel from 'primevue/sidebarpanel';
import SidebarRail from 'primevue/sidebarrail';
import SidebarSpacer from 'primevue/sidebarspacer';
import SidebarTrigger from 'primevue/sidebartrigger';
import Tooltip from 'primevue/tooltip';
import { SETTINGS_ALL_SHOPS_QUERY_VALUE } from '@/ShopManagement/settings.js';
import { ADMIN_WORKSPACE } from '@/BayHiveAdmin/workspace.js';

export default {
  inject: { adminWorkspace: { from: ADMIN_WORKSPACE, default: null } },
  components: {
    Button,
    Link,
    Sidebar,
    SidebarAside,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarPanel,
    SidebarRail,
    SidebarSpacer,
    SidebarTrigger,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    sidebarId: {
      type: String,
      default: 'shop-management-sidebar',
    },
    items: {
      type: Array,
      default: () => [],
    },
    currentPageKey: {
      type: String,
      default: 'dashboard',
    },
    selectedScope: {
      type: Object,
      default: null,
    },
    isMobileShell: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['navigate', 'update:open'],

  computed: {
    productName() {
      return activeBrand.value.productName;
    },
    isAdmin() {
      return this.adminWorkspace?.active.value === true;
    },
    isCollapsed() {
      return !this.open && !this.isMobileShell;
    },
    masterConfig() {
      return this.$page.props.master_config || {};
    },

    navigationGroups() {
      if (this.isAdmin) {
        const groups = new Map();
        for (const entry of this.$page.props.adminNavigation || []) {
          if (!groups.has(entry.group)) groups.set(entry.group, { label: entry.group, items: [] });
          groups.get(entry.group).items.push({
            ...entry,
            href: this.adminWorkspace.href(entry.href),
            isActive: entry.key === this.adminWorkspace.context.value?.workspace,
          });
        }
        // A collapsed rail is one continuous icon list; hidden group headings
        // otherwise retain their height and leave empty gaps between icons.
        if (this.isCollapsed && groups.size)
          return [{ items: [...groups.values()].flatMap((group) => group.items) }];
        return [...groups.values()];
      }

      const workspaceItems = [];

      this.items.forEach((item) => {
        const option = {
          ...item,
          href: this.itemHref(item),
          isActive: item.key === this.currentPageKey,
        };

        if (item.key === 'settings') {
          return;
        }

        workspaceItems.push(option);
      });

      return [
        workspaceItems.length
          ? {
              items: workspaceItems,
            }
          : null,
      ].filter(Boolean);
    },

    settingsItem() {
      if (this.isAdmin)
        return {
          key: 'return',
          label: 'Back to BayHive',
          icon: 'fa-solid fa-arrow-left',
          href: this.adminWorkspace.context.value.returnUrl,
          isActive: false,
        };
      const item = this.items.find((entry) => entry.key === 'settings');

      if (!item) {
        return null;
      }

      return {
        ...item,
        href: this.itemHref(item),
        isActive: item.key === this.currentPageKey,
      };
    },

    // The app shell is intentionally a dark navigation surface in either color
    // mode. These colors stay local to this Sidebar instance; Lara continues
    // to own its geometry, typography, radii, and interaction mechanics.
    sidebarDesignTokens() {
      return {
        ...(this.isAdmin && !this.isCollapsed
          ? {
              group: { padding: '0 {bayhive.spacing.sm}' },
              groupLabel: { height: '1.5rem' },
            }
          : {}),
        root: {
          borderColor: '{surface.800}',
          focusRing: {
            width: 'var(--p-bayhive-sidebar-focus-ring-width, {focus.ring.width})',
            color: 'var(--p-bayhive-sidebar-focus-ring-color, {primary.color})',
            shadow: 'var(--p-bayhive-sidebar-focus-ring-shadow, {focus.ring.shadow})',
          },
        },
        panel: {
          background: '{surface.950}',
          color: '{surface.50}',
        },
        menuButton: {
          fontSize: '1rem',
          height: '2.5rem',
          iconOnlyWidth: '2.5rem',
          color: '{surface.300}',
          focusBackground: 'color-mix(in srgb, {primary.color}, transparent 88%)',
          focusColor: '{surface.0}',
          activeBackground: 'color-mix(in srgb, {primary.color}, transparent 82%)',
          activeColor: '{surface.0}',
          icon: {
            color: '{surface.400}',
            focusColor: 'var(--p-bayhive-sidebar-active-icon-color, {bayhive.gold.200})',
          },
        },
      };
    },

    // The application may use a light page theme while this navigation surface
    // remains dark. Scope the Button's text-secondary tokens to this one
    // SidebarTrigger so its default, hover, active, and focus states retain
    // sufficient contrast without changing Buttons anywhere else.
    sidebarToggleDesignTokens() {
      return {
        secondary: {
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem var(--p-bayhive-sidebar-focus-ring-color, color-mix(in srgb, {bayhive.gold.200}, transparent 15%))',
          },
        },
        text: {
          secondary: {
            color: '{surface.0}',
            hoverBackground: 'color-mix(in srgb, {surface.0}, transparent 88%)',
            activeBackground: 'color-mix(in srgb, {surface.0}, transparent 80%)',
          },
        },
      };
    },

    // Sidebar deliberately owns its root attrs, so the classes supplied to its
    // Vue component are not forwarded. Use its supported root Pass Through
    // entry to keep the shell and collapsed-state classes on the actual rail.
    sidebarPassThrough() {
      return {
        root: {
          class: [
            'shop-management-shell-sidebar',
            {
              'shop-management-shell-sidebar--mobile': this.isMobileShell,
              'shop-management-sidebar--collapsed': this.isCollapsed,
            },
          ],
        },
      };
    },
  },

  methods: {
    handleNavigate(option, event) {
      if (!option?.href || option.href === '#') {
        event?.preventDefault();
        return;
      }

      this.$emit('navigate');

      if (this.isMobileShell) {
        this.$emit('update:open', false);
      }
    },

    itemHref(item) {
      if (!this.masterConfig?.shop_id) {
        return '#';
      }

      const href = route(item.routeName, {
        shop_id: this.masterConfig.shop_id,
        ...item.routeParams,
      });

      if (!this.selectedScope?.isAllShops) {
        return href;
      }

      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const url = new URL(href, baseUrl);
      url.searchParams.set('scope', SETTINGS_ALL_SHOPS_QUERY_VALUE);

      return typeof window !== 'undefined' ? url.toString() : url.pathname + url.search;
    },

    handleOpenChange(value) {
      this.$emit('update:open', value);
    },

    toggleLabel() {
      return this.open ? 'Collapse navigation' : 'Expand navigation';
    },

    itemTooltip(label) {
      if (!this.isCollapsed) return null;

      return {
        value: label,
        showDelay: 0,
        hideDelay: 0,
      };
    },
  },
};
</script>

<template>
  <Sidebar
    :id="sidebarId"
    :dt="sidebarDesignTokens"
    :pt="sidebarPassThrough"
    side="left"
    variant="sidebar"
    :collapsible="isMobileShell ? 'offcanvas' : 'icon'"
    :overlay="isMobileShell"
    :open="open"
    width="var(--p-bayhive-layout-sidebar-width, 18rem)"
    iconWidth="var(--p-bayhive-layout-sidebar-icon-width, 4.5rem)"
    @update:open="handleOpenChange"
  >
    <SidebarSpacer v-if="!isMobileShell" />
    <SidebarAside>
      <SidebarPanel>
        <SidebarHeader>
          <div class="shop-management-sidebar-header-row">
            <SidebarTrigger
              v-if="!isMobileShell"
              :target="sidebarId"
              asChild
              v-slot="{ class: triggerClass, a11yAttrs, onClick }"
            >
              <Button
                type="button"
                :class="[triggerClass, 'shop-management-sidebar-toggle']"
                :dt="sidebarToggleDesignTokens"
                iconOnly
                variant="text"
                rounded
                severity="secondary"
                size="large"
                :aria-label="toggleLabel()"
                :aria-controls="sidebarId"
                :aria-expanded="open"
                v-bind="a11yAttrs"
                @click="onClick"
              >
                <i
                  :class="[
                    'shop-management-sidebar-toggle-icon',
                    open ? 'fa-solid fa-angles-left' : 'fa-solid fa-angles-right',
                  ]"
                  aria-hidden="true"
                />
              </Button>
            </SidebarTrigger>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <nav
            :aria-label="isAdmin ? 'BayHive Super Admin navigation' : `${productName} navigation`"
            class="shop-management-sidebar-nav"
            :class="{ 'shop-management-sidebar-nav--compact': isAdmin && !isCollapsed }"
          >
            <SidebarGroup v-for="(group, index) in navigationGroups" :key="group.label || index">
              <SidebarGroupLabel v-if="group.label">{{ group.label }}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem v-for="item in group.items" :key="item.key">
                    <SidebarMenuButton :isActive="item.isActive" asChild v-slot="{ class: buttonClass, a11yAttrs }">
                      <Link
                        :href="item.href"
                        :class="[
                          buttonClass,
                          'shop-management-sidebar-nav-link',
                        ]"
                        :aria-label="item.label"
                        v-tooltip.right="itemTooltip(item.label)"
                        :aria-current="item.isActive ? 'page' : undefined"
                        v-bind="a11yAttrs"
                        @click="handleNavigate(item, $event)"
                      >
                        <span class="shop-management-sidebar-nav-link-icon" aria-hidden="true">
                          <i :class="item.icon" />
                        </span>
                        <span class="shop-management-sidebar-nav-link-label">{{ item.label }}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </nav>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu v-if="settingsItem">
            <SidebarMenuItem>
              <SidebarMenuButton
                :isActive="settingsItem.isActive"
                asChild
                v-slot="{ class: buttonClass, a11yAttrs }"
              >
                <Link
                  :href="settingsItem.href"
                  :class="[
                    buttonClass,
                    'shop-management-sidebar-nav-link',
                  ]"
                  :aria-label="settingsItem.label"
                  v-tooltip.right="itemTooltip(settingsItem.label)"
                  :aria-current="settingsItem.isActive ? 'page' : undefined"
                  v-bind="a11yAttrs"
                  @click="handleNavigate(settingsItem, $event)"
                >
                  <span class="shop-management-sidebar-nav-link-icon" aria-hidden="true">
                    <i :class="settingsItem.icon" />
                  </span>
                  <span class="shop-management-sidebar-nav-link-label">{{ settingsItem.label }}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <!-- Build version hidden for now — its placement in the shop-management
               sidebar needs work. Re-enable by restoring the appVersion guard. -->
          <div
            v-if="false && $page.props.appVersion && $page.props.appVersion !== 'unknown'"
            class="sidebar-build-version"
          >
            {{ $page.props.appVersion }}
          </div>
        </SidebarFooter>

        <SidebarRail v-if="!isMobileShell" />
      </SidebarPanel>
    </SidebarAside>
  </Sidebar>
</template>

<style scoped>
.sidebar-build-version {
  padding: 4px 8px;
  font-size: 10px;
  color: var(--p-text-muted-color, rgba(0, 0, 0, 0.35));
  font-family: monospace;
  text-align: center;
}

.shop-management-sidebar-header-row {
  display: flex;
  align-items: center;
  inline-size: 100%;
  justify-content: flex-end;
}

.shop-management-sidebar--collapsed .shop-management-sidebar-header-row {
  justify-content: center;
}

.shop-management-sidebar-toggle-icon {
  display: block;
  line-height: 1;
}

.shop-management-sidebar-nav-link {
  align-items: center;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  text-decoration: none;
}

.shop-management-sidebar-nav-link-icon {
  align-items: center;
  color: var(--p-sidebar-menu-button-icon-color);
  display: inline-flex;
  justify-content: center;
}

/* Sidebar tokens style PrimeVue SVG icons, while the app intentionally uses
   Font Awesome glyphs. Keep this one bridge inside the owned shell sidebar. */
.shop-management-shell-sidebar :deep(
  .p-sidebar-menu-button:is(:hover, :focus-visible, :active, [data-active='true'])
  .shop-management-sidebar-nav-link-icon
) {
  color: var(--p-sidebar-menu-button-icon-focus-color);
}

.shop-management-sidebar-nav-link-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.shop-management-sidebar--collapsed .shop-management-sidebar-nav-link {
  align-items: center;
  display: flex;
  justify-content: center;
}

.shop-management-sidebar--collapsed .shop-management-sidebar-nav-link-label {
  display: none;
}

.shop-management-sidebar--collapsed :deep(.p-sidebar-group) {
  padding-inline: 0;
}

.shop-management-sidebar--collapsed :deep(.p-sidebar-menu-item) {
  display: flex;
  justify-content: center;
}

.shop-management-sidebar-nav {
  display: grid;
  gap: 0.85rem;
}

.shop-management-sidebar-nav--compact {
  gap: 0;
}

.shop-management-shell-sidebar {
  align-self: flex-start;
  height: 100vh;
  height: 100dvh;
  position: sticky;
  top: 0;
}

.shop-management-shell-sidebar--mobile {
  inset-block: 0;
  inset-inline-start: 0;
  position: fixed;
}

:deep(.p-sidebar-content) {
  overscroll-behavior-y: auto;
}

:deep(.shop-management-sidebar-nav .p-sidebar-menu),
:deep(.shop-management-sidebar-nav .p-sidebar-group-content) {
  gap: 0.35rem;
}
</style>
